import React, { useEffect, useState } from "react";
import type { ExamItem, ReviewItem, Task } from "@nelurio/contracts";
import { Badge, Button, Card, Icon } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { Link } from "react-router";
import { ReviewBadge } from "./ReviewBadge";
import { fetchRoutines, removeRoutine, routinesForDate, toggleRoutineDoneApi, type Routine } from "../lib/routines";
import { hasDetails, normalizeDescription, toggleExpanded } from "../lib/taskDetails";
import { notifyDataChanged } from "../hooks/useCourses";
import { categoryLabel } from "../lib/categories";
import { TaskComposer } from "./TaskComposer";
import { api, isDemoMode } from "../api/client";

function localDateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function taskTone(kind: Task["kind"]) {
  if (kind === "exam") return "coral";
  if (kind === "assignment") return "amber";
  if (kind === "new_lesson") return "blue";
  if (kind === "class") return "green";
  return "purple";
}



interface TodayPlanProps {
  scheduled?: Task[] | null;
  anytime?: Task[] | null;
  reviews?: ReviewItem[] | null;
  /** Course exams falling on the day — course exams have no task row. */
  exams?: ExamItem[] | null;
  /** Quick-add creates tasks for this date instead of today. */
  date?: string;
  /** Day-panel actions: "back to today" while a calendar day is selected. */
  headerAction?: { label: string; onClick: () => void };
  /** The selected day's fetch failed — panel would otherwise read as empty. */
  loadFailed?: boolean;
  onRetry?: () => void;
}

export function TodayPlan({ scheduled, anytime, reviews, exams, date, headerAction, loadFailed, onRetry }: TodayPlanProps) {
  const { t, formatNumber, locale } = useI18n();
  const [completed, setCompleted] = useState<Set<string>>(() => new Set());
  const [doneReviews, setDoneReviews] = useState<Set<string>>(() => new Set());
  const [tasks, setTasks] = useState<Task[]>([]);
  const [reopened, setReopened] = useState<Set<string>>(() => new Set());
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [routineDone, setRoutineDone] = useState<Set<string>>(() => new Set());
  const [composerOpen, setComposerOpen] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);
  const [editingRoutine, setEditingRoutine] = useState<Routine | null>(null);

  const todayKey = date ?? localDateKey(new Date());

  // Day switch must start from a clean slate: optimistic adds and routine
  // ticks belong to the day they were made on — leaking them into another
  // day's panel silently misrendered both.
  useEffect(() => {
    setTasks([]);
    setCompleted(new Set());
    setReopened(new Set());
    setRoutineDone(new Set());
  }, [todayKey]);

  // Server routines: same list on every device; refresh on focus/data events.
  useEffect(() => {
    if (!isDemoMode()) {
      fetchRoutines(todayKey).then((data) => { setRoutines(data.routines); setRoutineDone(new Set(data.done)); }).catch(() => undefined);
    } else {
      try {
        const local = JSON.parse(window.localStorage.getItem("nelurio.routines.v1") ?? "[]");
        setRoutines(Array.isArray(local) ? local : []);
      } catch { setRoutines([]); }
    }
  }, [todayKey]);

  const isReviewDone = (review: ReviewItem) => Boolean(review.completedAt) || doneReviews.has(review.id);
  const completeReview = async (review: ReviewItem) => {
    // Checked stays visible all day (strikethrough) — nothing disappears.
    const nextDone = !isReviewDone(review);
    setDoneReviews((current) => {
      const next = new Set(current);
      if (nextDone) next.add(review.id);
      else next.delete(review.id);
      return next;
    });
    if (!isDemoMode()) {
      // Both directions persist: complete (easy) and reopen — unchecking used to
      // stay local-only and silently revert on the next poll.
      try {
        if (nextDone) {
          await api(`/reviews/${review.id}/complete`, { method: "POST", body: JSON.stringify({ difficulty: "easy" }) });
        } else {
          await api(`/reviews/${review.id}/complete`, { method: "DELETE" });
        }
        notifyDataChanged();
      } catch {
        // Roll back to the PREVIOUS membership (an unchecked review re-adds).
        setDoneReviews((current) => {
          const next = new Set(current);
          if (nextDone) next.delete(review.id);
          else next.add(review.id);
          return next;
        });
      }
    }
  };

  const viewDate = new Date(`${todayKey}T12:00:00`);
  const dayRoutines = routinesForDate(routines, viewDate);
  const doneRoutines = routineDone;
  const serverScheduled = Array.isArray(scheduled) ? scheduled : [];
  const serverAnytime = Array.isArray(anytime) ? anytime : [];
  const serverAll = [...serverScheduled, ...serverAnytime];
  const serverIds = new Set(serverAll.map((task) => task.id));
  const allTasks = [...tasks.filter((task) => task.dueDate === todayKey && !serverIds.has(task.id)), ...serverAll];
  // Review/course items stay in the top section — the todo list is only the
  // user's own entries.
  const todoTasks = allTasks.filter((task) => task.kind !== "class" && task.kind !== "new_lesson" && task.kind !== "exam");
  const reviewItems = Array.isArray(reviews) ? reviews : [];
  // Exams of the day: course-level exams (courses.exam_date) + any task saved
  // with kind "exam" — both belong on the clicked day's panel, not just as a
  // calendar dot.
  const examItems: ExamItem[] = (() => {
    const courseExams = Array.isArray(exams) ? exams : [];
    // A kind=exam task pointing at a course that already has an exam row for
    // this day (same course, same title) is the SAME exam — rendering both
    // double-counted it. Distinctly-titled exam tasks stay (real extra items).
    return [
      ...courseExams,
      ...allTasks
        .filter((task) => task.kind === "exam")
        .filter((task) => !(task.courseId && courseExams.some((exam) => exam.courseId === task.courseId && exam.title === task.title)))
        .map((task) => ({ id: task.id, courseId: task.courseId ?? "", courseTitle: task.courseTitle ?? "", title: task.title, examDate: task.dueDate })),
    ];
  })();
  const dayLabel = headerAction
    ? new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long" }).format(new Date(`${todayKey}T12:00:00`))
    : "";

  const isTaskDone = (task: Task) => completed.has(task.id) || (task.status === "completed" && !reopened.has(task.id));
  // Routines scheduled today are part of "today's work" — the ring and the
    // section badge must agree (audit F1: the ring silently skipped them).
    const routinesDone = dayRoutines.filter((routine) => routineDone.has(routine.id)).length;
    const doneCount = todoTasks.filter(isTaskDone).length + routinesDone + reviewItems.filter((review) => isReviewDone(review)).length;
  // Every item stays visible after checking, so the denominator is simply the
  // item count — done items are part of it exactly once.
  const totalItems = todoTasks.length + dayRoutines.length + reviewItems.length;
  const ratio = totalItems > 0 ? Math.round((doneCount / totalItems) * 100) : 0;

  const toggleTask = async (task: Task) => {
    const isDone = isTaskDone(task);
    setCompleted((current) => {
      const next = new Set(current);
      if (isDone) next.delete(task.id);
      else next.add(task.id);
      return next;
    });
    if (task.status === "completed") setReopened((current) => new Set(current).add(task.id));
    if (!isDemoMode()) {
      try {
        await api(`/tasks/${task.id}`, { method: "PATCH", body: JSON.stringify({ status: isDone ? "open" : "completed" }) });
        notifyDataChanged();
      } catch {
        setCompleted((current) => {
          const next = new Set(current);
          if (isDone) next.add(task.id);
          else next.delete(task.id);
          return next;
        });
      }
    }
  };

  const deleteTask = async (task: Task) => {
    setTasks((current) => current.filter((item) => item.id !== task.id));
    if (!isDemoMode()) {
      try {
        await api(`/tasks/${task.id}`, { method: "DELETE" });
        notifyDataChanged();
      } catch {
        // Restore on failure — the task used to vanish locally then resurrect on poll.
        setTasks((current) => (current.some((item) => item.id === task.id) ? current : [...current, task]));
      }
    }
  };

  const deleteRoutineRow = async (routine: Routine) => {
    setRoutines((current) => current.filter((item) => item.id !== routine.id));
    if (!isDemoMode()) {
      try {
        await removeRoutine(routine.id);
        notifyDataChanged();
      } catch {
        fetchRoutines(todayKey).then((data) => { setRoutines(data.routines); }).catch(() => undefined);
      }
    }
  };

  const onSaved = (task: Task | null, routine?: Routine) => {
    if (routine) {
      // Demo keeps ticks in state only — a refetch here returned done:[] and
      // visually unticked every routine the user had checked this session.
      if (isDemoMode()) return;
      fetchRoutines(todayKey).then((data) => { setRoutines(data.routines); setRoutineDone(new Set(data.done)); }).catch(() => undefined);
      return;
    }
    if (!task) return;
    setTasks((current) => {
      const exists = current.some((item) => item.id === task.id);
      return exists ? current.map((item) => (item.id === task.id ? task : item)) : [...current, task];
    });
    // A create/edit must reach EVERY surface at once: the calendar dots and
    // (when a day is clicked) the selected-day panel — the dashboard listens
    // for this event and refetches both. Without it the task only showed up
    // after the next poll while the calendar already carried the dot.
    if (!isDemoMode()) notifyDataChanged();
  };

  const [expandedDetails, setExpandedDetails] = useState<Set<string>>(() => new Set());

  const renderTodo = (task: Task) => {
    // Completed state comes from the server too — checked items survive a
    // refresh and read like a daily journal.
    const isDone = completed.has(task.id) || (task.status === "completed" && !reopened.has(task.id));
    return (
      <article className={`today-task ${isDone ? "is-completed" : ""}`} key={task.id}>
        <button className="today-task__check" aria-label={isDone ? t("task.reopen") : t("common.completed")} onClick={() => void toggleTask(task)}>
          <Icon name="check" size={15} />
        </button>
        <div className="today-task__body">
          {hasDetails(task) ? (
            <button type="button" className="task-details-toggle" aria-expanded={expandedDetails.has(task.id)} title={t("task.details")} onClick={() => setExpandedDetails((ids) => toggleExpanded(ids, task.id))}>
              <strong>{task.title}</strong>
            </button>
          ) : (
            <strong>{task.title}</strong>
          )}
          <small>
            {task.courseTitle}
            {task.durationMinutes ? ` · ${formatNumber(task.durationMinutes)} ${t("common.minutes")}` : ""}
          </small>
          {expandedDetails.has(task.id) && <p className="task-desc">{normalizeDescription(task.description)}</p>}
          <div className="today-task__tags">
            <Badge tone={taskTone(task.kind)}>{categoryLabel(task.kind, t)}</Badge>
          </div>
        </div>
        <time>{task.dueTime || t("common.noTime")}</time>
        <span className="simple-task__actions">
          <button onClick={() => { setEditing(task); setComposerOpen(true); }} aria-label={t("common.edit")}><Icon name="edit" size={13} /></button>
          <button onClick={() => void deleteTask(task)} aria-label={t("common.delete")}><Icon name="trash" size={13} /></button>
        </span>
      </article>
    );
  };

  return (
    <Card className="today-plan" elevated>
      <TaskComposer
        open={composerOpen}
        dateKey={todayKey}
        task={editing}
        routine={editingRoutine}
        onClose={() => { setComposerOpen(false); setEditing(null); setEditingRoutine(null); }}
        onSaved={onSaved}
      />
      <div className="today-plan__head">
        <div className="today-plan__head-row">
          <div>
            <span className="section-kicker">
              {headerAction ? dayLabel : t("common.today")}
              {headerAction && (
                <button className="today-plan__today-chip" onClick={headerAction.onClick} aria-label={headerAction.label}>
                  <Icon name="x" size={11} />
                  <span className="today-plan__today-chip-label">{headerAction.label}</span>
                </button>
              )}
            </span>
            <h2>{t("dashboard.todayPlan")}</h2>
          </div>
          {totalItems > 0 && (
            <div className="today-plan__progress" title={`${formatNumber(doneCount)} / ${formatNumber(totalItems)}`}>
              <span><i style={{ width: `${ratio}%` }} /></span>
              <small>{formatNumber(doneCount)} / {formatNumber(totalItems)} · {formatNumber(ratio)}%</small>
            </div>
          )}
        </div>
        <Button icon="plus" className="today-plan__add" onClick={() => { setEditing(null); setEditingRoutine(null); setComposerOpen(true); }}>
          {t("dashboard.addTask")}
        </Button>
      </div>

      {loadFailed && (
        <div className="today-plan__loadfailed" role="alert">
          <span>{t("todayplan.dayLoadFailed")}</span>
          <Button onClick={onRetry}>{t("common.retry")}</Button>
        </div>
      )}

      {/* Top block: the course rhythm — reviews stay listed all day. */}
      {reviewItems.length > 0 && (
        <div className="today-plan__section">
          <div className="today-plan__section-title">
            <strong>{t("reviews.title")}</strong>
            <Badge tone="purple">{formatNumber(reviewItems.length)}</Badge>
          </div>
          {reviewItems.map((review) => {
            const done = isReviewDone(review);
            return (
              <article className={`today-task review-inline ${done ? "is-completed" : ""}`} key={review.id}>
                <button className={`today-task__check ${done ? "is-checked" : ""}`} title={t("reviews.easy")} onClick={() => void completeReview(review)}>
                  <Icon name="check" size={14} />
                </button>
                <div className="today-task__body">
                  <strong>{review.title}</strong>
                  <small>{review.courseTitle}</small>
                </div>
                <ReviewBadge stage={review.stage} />
              </article>
            );
          })}
        </div>
      )}

      {/* Exams block: everything exam-shaped registered on this day. */}
      {examItems.length > 0 && (
        <div className="today-plan__section">
          <div className="today-plan__section-title">
            <strong>{t("todayplan.exams")}</strong>
            <Badge tone="coral">{formatNumber(examItems.length)}</Badge>
          </div>
          {examItems.map((exam) => (
            <article className="today-task" key={exam.id}>
              <span className="today-task__check is-exam" aria-hidden="true"><Icon name="star" size={14} /></span>
              <div className="today-task__body">
                <strong>{exam.title}</strong>
                {exam.courseTitle && <small>{exam.courseTitle}</small>}
              </div>
              <Badge tone="coral">{t("todayplan.examBadge")}</Badge>
            </article>
          ))}
        </div>
      )}

      {/* Bottom block: today's to-do list — tasks + routines as one list. */}
      <div className="today-plan__section">
        <div className="today-plan__section-title">
          <strong>{t("tasks.todoDivider")}</strong>
          <Badge tone="blue">{formatNumber(todoTasks.length + dayRoutines.length)}</Badge>
        </div>
        {todoTasks.map(renderTodo)}
        {dayRoutines.map((routine) => {
          const done = doneRoutines.has(routine.id);
          return (
            <article className={`today-task routine-inline ${done ? "is-completed" : ""}`} key={routine.id}>
              <button className={`today-task__check ${done ? "is-checked" : ""}`} aria-label={t("common.completed")} onClick={() => { const next = new Set(routineDone); if (done) next.delete(routine.id); else next.add(routine.id); setRoutineDone(next); void (async () => {
                try {
                  await toggleRoutineDoneApi(routine.id, todayKey, !done);
                } catch {
                  // Roll the optimistic tick back so it matches the server.
                  setRoutineDone((current) => {
                    const rollback = new Set(current);
                    if (done) rollback.add(routine.id);
                    else rollback.delete(routine.id);
                    return rollback;
                  });
                }
              })(); }}>
                <Icon name="check" size={15} />
              </button>
              <div className="today-task__body">
                <strong>{routine.title}</strong>
                <div className="today-task__tags"><Badge tone="blue">{categoryLabel(routine.kind, t)}</Badge>{routine.time && <Badge tone="purple"><Icon name="clock" size={11} />{routine.time}</Badge>}</div>
              </div>
              <div className="today-task__row-actions">
                <button type="button" aria-label={t("common.edit")} title={t("common.edit")} onClick={() => { setEditingRoutine(routine); setEditing(null); setComposerOpen(true); }}>
                  <Icon name="edit" size={13} />
                </button>
                <button type="button" aria-label={t("common.delete")} title={t("common.delete")} onClick={() => void deleteRoutineRow(routine)}>
                  <Icon name="trash" size={13} />
                </button>
              </div>
              <time>{routine.time || t("common.noTime")}</time>
            </article>
          );
        })}
        {!todoTasks.length && !dayRoutines.length && <p className="today-plan__empty">{t("dashboard.planEmpty")}</p>}
      </div>
    </Card>
  );
}
